import { inject, injectable } from "inversify";
import { type IService, Logger } from "@designboticteam/core";
import { type BoundsDefinition, type MapCameraProjection, MapConfigService, MapStoreService } from "@designboticteam/map";
import { MapLibreInstanceService } from "../MapLibreInstanceService";
import { DEFAULT_VIEW, FIT_BOUNDS_DURATION, ORTHOGRAPHIC_FOV, PERSPECTIVE_FOV, VIEW_PRESETS } from "./constants";
import type { MapCameraPadding, MapCameraPreset } from "./types";
import { getAdjacentScaleDenominator } from "./utils/getAdjacentScaleDenominator";
import { getScaleDenominator } from "./utils/getScaleDenominator";
import { getZoomForScaleDenominator } from "./utils/getZoomForScaleDenominator";

@injectable()
class MapLibreCameraViewService implements IService {
	readonly serviceName = "📷 MapLibreCameraViewService";
	private logger = new Logger(`🌍 MAP | ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#00aa00" });

	constructor(
		@inject(MapConfigService) private mapConfigService: MapConfigService,
		@inject(MapLibreInstanceService) private mapLibreInstanceService: MapLibreInstanceService,
		@inject(MapStoreService) private mapStoreService: MapStoreService,
	) {}

	public initialize(): void {
		// `move` covers panning, zooming, rotating and tilting alike.
		this.mapLibreInstanceService.map.on("move", this.syncCameraState);
		this.syncCameraState();

		this.logger.log("initialized");
	}

	public dispose(): void {
		this.mapLibreInstanceService.map.off("move", this.syncCameraState);

		this.logger.log("disposed");
	}

	public zoomIn(): void {
		this.zoomToAdjacentScale("in");
	}

	public zoomOut(): void {
		this.zoomToAdjacentScale("out");
	}

	public resetBearing(): void {
		this.mapLibreInstanceService.map.easeTo({ bearing: 0 });
	}

	public resetPitch(): void {
		this.mapLibreInstanceService.map.easeTo({ pitch: 0 });
	}

	/**
	 * MapLibre has no orthographic projection — narrowing the field of view pushes the camera back
	 * while holding the centre scale, which is as close to one as the map gets.
	 */
	public setProjection(projection: MapCameraProjection): void {
		this.mapLibreInstanceService.map.setVerticalFieldOfView(projection === "orthographic" ? ORTHOGRAPHIC_FOV : PERSPECTIVE_FOV);
		this.mapStoreService.setState((state) => ({ camera: { ...state.camera, projection } }));
	}

	public resetView(): void {
		this.loadViewPreset(DEFAULT_VIEW);
	}

	public loadViewPreset(preset: MapCameraPreset): void {
		this.logger.log(`Loading camera preset: ${preset}`);

		const { zoom, pitch, bearing } = VIEW_PRESETS[preset];
		const center = this.mapConfigService.origin;

		this.mapLibreInstanceService.map.easeTo({ center, zoom, pitch, bearing });
	}

	public flyTo({ center, zoom }: { center: [number, number]; zoom?: number }): void {
		this.mapLibreInstanceService.map.flyTo({ center, zoom });
	}

	/**
	 * Frames a geographic extent. `padding` is the caller's business — it depends on which parts of the
	 * viewport its UI covers.
	 */
	public fitBounds({ bounds, padding, maxZoom }: { bounds: BoundsDefinition; padding: MapCameraPadding; maxZoom?: number }): void {
		// `cameraForBounds` solves the fit for an unpitched view and returns no pitch, so level the camera to
		// land exactly on what it computed. `linear` keeps it a direct ease instead of flyTo's zoom-out arc.
		this.mapLibreInstanceService.map.fitBounds([bounds.west, bounds.south, bounds.east, bounds.north], {
			padding,
			maxZoom,
			pitch: 0,
			linear: true,
			duration: FIT_BOUNDS_DURATION,
		});
	}

	/** Steps to the neighbouring rung of the scale ladder, so the readout always lands on a round ratio. */
	private zoomToAdjacentScale(direction: "in" | "out"): void {
		const map = this.mapLibreInstanceService.map;
		const latitude = map.getCenter().lat;
		const tileSize = map.transform.tileSize;

		const scaleDenominator = getAdjacentScaleDenominator({
			scaleDenominator: getScaleDenominator({ zoom: map.getZoom(), latitude, tileSize }),
			direction,
		});

		if (scaleDenominator === null) {
			return;
		}

		map.easeTo({ zoom: getZoomForScaleDenominator({ scaleDenominator, latitude, tileSize }) });
	}

	private syncCameraState = (): void => {
		const map = this.mapLibreInstanceService.map;
		const scaleDenominator = getScaleDenominator({ zoom: map.getZoom(), latitude: map.getCenter().lat, tileSize: map.transform.tileSize });

		this.mapStoreService.setState((state) => ({
			camera: {
				...state.camera,
				bearing: map.getBearing(),
				pitch: map.getPitch(),
				// Rounding keeps the value stable while panning, which spares the subscribers a re-render per frame.
				scaleDenominator: Math.round(scaleDenominator),
			},
		}));
	};
}

export { MapLibreCameraViewService };
